import type {JSONNode} from '@packages/shared/types/JSONNode';
import {
  ComponentExport,
  LibraryContent,
  SymbolsLocalizationData,
} from '../types';
import {
  Expression,
  TRecord,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {RawFileType} from '@packages/systems/core/records/FileRecord';
import {getMd5Hash} from '@packages/utilities/md5-browser';
import {LIBRARY_EXPORT_TYPES} from '../constants';
import {withMeta, withSystem} from '@packages/systems/wfdl';
import {pluginName, qualify, typeName} from '@packages/systems/wfdl-Qualified';

const imageType = qualify(pluginName('Asset'), typeName('Image'));
export const libraryID = '66ec3605c8bd3b3330903951';
export const slug = 'library-with-localization';

const component_ID_1 = 'component_ID_1';
const component_ID_2 = 'component_ID_2';
const component_ID_3 = 'component_ID_3';
const component_ID_4 = 'component_ID_4';
const component_ID_5 = 'component_ID_5';
const component_ID_6 = 'ea094b37-29dc-c4cd-f884-fdb56fc1d27e';
const component_ID_7 = '414c3edc-6795-c3f9-fdc5-6f85e4ce65e7';
const component_ID_8 = 'ad21ce94-6d93-ecb1-32ef-47772be6f896';
const component_ID_9 = '6893c772-0d47-d655-16e8-584d8bc06e0e';
const component_ID_10 = '6893c772-0d47-d655-16e8-584d8bc00e10';

const asset_ID_1 = 'asset_ID_1';
const asset_ID_2 = 'asset_ID_2';
const asset_ID_3 = 'asset_ID_3';
const asset_ID_4 = 'asset_ID_4';
const asset_ID_5 = 'asset_ID_5';
const asset_ID_6 = 'asset_ID_6';
const asset_ID_7 = 'asset_ID_7';
const asset_ID_8 = 'asset_ID_8';
const asset_ID_9 = 'asset_ID_9';

export const IDs = {
  component_ID_1,
  component_ID_2,
  component_ID_3,
  component_ID_4,
  component_ID_5,
  component_ID_6,
  component_ID_7,
  component_ID_8,
  component_ID_9,
  asset_ID_1,
  asset_ID_2,
  asset_ID_3,
  asset_ID_4,
  asset_ID_5,
  asset_ID_6,
  asset_ID_7,
  asset_ID_8,
  asset_ID_9,
};

const generateAsset = (id: string): RawFileType => ({
  versionedResource: libraryID,
  s3Url: `https://s3.amazonaws.com/webflow-dev-assets/${libraryID}/${id}_fileName.png`,
  origFileName: 'fileName.png',
  fileName: `${id}_fileName.png`,
  fileHash: getMd5Hash(id),
  mimeType: 'image/png',
  createdOn: '2024-09-19T14:32:37.707Z',
  _id: id,
});

export const assets: Record<string, RawFileType> = {
  [asset_ID_1]: generateAsset(asset_ID_1),
  [asset_ID_2]: generateAsset(asset_ID_2),
  [asset_ID_3]: generateAsset(asset_ID_3),
  [asset_ID_4]: generateAsset(asset_ID_4),
  [asset_ID_5]: generateAsset(asset_ID_5),
  [asset_ID_6]: generateAsset(asset_ID_6),
  [asset_ID_7]: generateAsset(asset_ID_7),
  [asset_ID_8]: {
    ...generateAsset(asset_ID_8),
    alt: 'This is an asset description!',
    localizedSettings: {
      pt: {
        alt: 'Esta é uma descrição do ativo!',
      },
    },
  },
  [asset_ID_9]: generateAsset(asset_ID_9),
};

export const symbols: JSONNode[] = [
  {
    _id: component_ID_1,
    type: 'BlockContainer',
    tag: 'div',
    classes: [],
    children: [
      'bfd3fec7-8d8d-129e-0def-7d2f1838d30f',
      'a2f613ab-0b3d-7b6d-c59c-171171217d36',
    ],
    data: {
      grid: {
        type: 'container',
      },
      tag: 'div',
      sym: {
        root: true,
        name: 'c1',
        copies: {
          '66e1f03b0cea568b35980fcf': 1,
        },
      },
    },
    dataType:
      '{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowEmpty"}}}',
  },
  {
    _id: 'bfd3fec7-8d8d-129e-0def-7d2f1838d30f',
    type: 'Heading',
    tag: 'h1',
    classes: [],
    children: ['bfd3fec7-8d8d-129e-0def-7d2f1838d310'],
    data: {
      tag: 'h1',
    },
  },
  {
    _id: 'bfd3fec7-8d8d-129e-0def-7d2f1838d310',
    text: true,
    v: 'This is a title',
  },
  {
    _id: 'a2f613ab-0b3d-7b6d-c59c-171171217d36',
    type: 'Symbol',
    tag: 'div',
    classes: [],
    children: [],
    data: {
      sym: {
        inst: component_ID_2,
      },
    },
  },
  {
    _id: component_ID_2,
    type: 'Heading',
    tag: 'h1',
    classes: ['12275ee2-d8c8-b42e-7ecd-68c7d636e279'],
    children: ['23b40dfe-0251-af0e-4efd-e47f1aa8be3d'],
    data: {
      tag: 'h1',
      sym: {
        root: true,
        name: 'c2',
      },
    },
    dataType:
      '{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowEmpty"}}}',
  },
  {
    _id: '23b40dfe-0251-af0e-4efd-e47f1aa8be3d',
    text: true,
    v: 'Localization is very cool',
  },
  {
    _id: component_ID_3,
    type: 'BlockContainer',
    tag: 'div',
    classes: [],
    children: [
      'b37f6f19-02ae-c266-ef57-a4de36df38e9',
      '76ce35f2-d749-a2ae-1299-d6b51880dc45',
    ],
    data: {
      grid: {
        type: 'container',
      },
      tag: 'div',
      sym: {
        root: true,
        name: 'c1',
        copies: {
          '66ec20b1768f52978c5cc46c': 1,
        },
      },
    },
    dataType:
      '{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowEmpty"}}}',
  },
  {
    _id: 'b37f6f19-02ae-c266-ef57-a4de36df38e9',
    type: 'Image',
    tag: 'img',
    classes: [],
    children: [],
    data: {
      attr: {
        src: 'https://cdn.dev.website-files.com/66ec3605c8bd3b3330903951/asset_ID_8_weimaraner-1801724_1280.jpg',
        loading: 'lazy',
        width: 'Auto',
        height: '200',
        alt: '__wf_reserved_inherit',
      },
      img: {
        id: asset_ID_8,
      },
      srcsetDisabled: false,
      sizes: [
        {
          max: 479,
          size: '100vw',
        },
        {
          max: 10000,
          size: '301.171875px',
        },
      ],
    },
  },
  {
    _id: '76ce35f2-d749-a2ae-1299-d6b51880dc45',
    type: 'Symbol',
    tag: 'div',
    classes: [],
    children: [],
    data: {
      sym: {
        inst: component_ID_4,
        instDataType: `{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowCons","val":{"label":"sym","type":{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowCons","val":{"label":"overrides","type":{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowCons","val":{"label":"d10f4fbe-f651-5953-242e-b428675976f0","type":{"type":"TypeConstructor","val":["Asset","Image"],"system":{"label":"Localized Image Prop 2","isBindable":true,"defaultValue":{"id":"${asset_ID_3}"},"isUnlinked":false,"isDefault":false},"meta":{"type":"image"}},"tail":{"type":"RowEmpty"}}}}},"tail":{"type":"RowEmpty"}}}}},"tail":{"type":"RowEmpty"}}}}}`,
        overrides: {
          'd10f4fbe-f651-5953-242e-b428675976f0': {
            id: asset_ID_4,
          },
        },
      },
    },
  },
  {
    _id: component_ID_4,
    type: 'BlockContainer',
    tag: 'div',
    classes: [],
    children: ['020237bc-46b3-358f-afe4-406f3c4a80b7'],
    data: {
      grid: {
        type: 'container',
      },
      tag: 'div',
      sym: {
        root: true,
        name: 'c2',
      },
    },
    dataType: `{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowCons","val":{"label":"d10f4fbe-f651-5953-242e-b428675976f0","type":{"type":"TypeConstructor","val":["Asset","Image"],"system":{"label":"Localized Image Prop 2","isBindable":true,"defaultValue":{"id":"${asset_ID_3}"},"isUnlinked":false,"isDefault":true},"meta":{"type":"image"}},"tail":{"type":"RowEmpty"}}}}}`,
  },
  {
    _id: '020237bc-46b3-358f-afe4-406f3c4a80b7',
    type: 'Symbol',
    tag: 'div',
    classes: [],
    children: [],
    data: {
      sym: {
        inst: component_ID_5,
        instDataType: `{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowCons","val":{"label":"sym","type":{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowCons","val":{"label":"overrides","type":{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowCons","val":{"label":"05bc9ceb-bfa3-f5b2-3e8c-8cef6d1a8517","type":{"type":"TypeConstructor","val":["Asset","Image"],"system":{"label":"Localized Image Prop 1","isBindable":true,"defaultValue":{"id":"${asset_ID_9}"},"isUnlinked":false,"isDefault":true},"meta":{"type":"image"}},"tail":{"type":"RowEmpty"}}}}},"tail":{"type":"RowEmpty"}}}}},"tail":{"type":"RowEmpty"}}}}}`,
      },
    },
    bind: {
      type: 'Record',
      val: {
        sym: {
          type: 'Record',
          val: {
            overrides: {
              type: 'Record',
              val: {
                '05bc9ceb-bfa3-f5b2-3e8c-8cef6d1a8517': {
                  type: 'Select',
                  val: {
                    from: {
                      type: 'Variable',
                      val: 'data',
                    },
                    prop: 'd10f4fbe-f651-5953-242e-b428675976f0',
                  },
                },
              },
            },
          },
        },
      },
    } as unknown as Expression,
  },
  {
    _id: component_ID_5,
    type: 'BlockContainer',
    tag: 'div',
    classes: [],
    children: [
      '5fcba5d9-73a7-fcb1-96f8-e9aa6cb9fd35',
      'c58fa894-f34b-adb3-c153-c0d1894a416b',
    ],
    data: {
      grid: {
        type: 'container',
      },
      tag: 'div',
      sym: {
        root: true,
        name: 'c3',
      },
    },
    dataType: `{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowCons","val":{"label":"05bc9ceb-bfa3-f5b2-3e8c-8cef6d1a8517","type":{"type":"TypeConstructor","val":["Asset","Image"],"system":{"label":"Localized Image Prop 1","isBindable":true,"defaultValue":{"id":"${asset_ID_9}"}},"meta":{"type":"image"}},"tail":{"type":"RowEmpty"}}}}}`,
  },
  {
    _id: '5fcba5d9-73a7-fcb1-96f8-e9aa6cb9fd35',
    type: 'Image',
    tag: 'img',
    classes: [],
    children: [],
    data: {
      attr: {
        src: 'https://cdn.dev.website-files.com/66ec3605c8bd3b3330903951/asset_ID_6_dogs-4688586_1280.jpg',
        loading: 'lazy',
        width: 'Auto',
        height: '200',
        alt: '__wf_reserved_inherit',
      },
      img: {
        id: asset_ID_6,
      },
      srcsetDisabled: false,
      sizes: [
        {
          max: 479,
          size: '100vw',
        },
        {
          max: 10000,
          size: '300.1171875px',
        },
      ],
    },
  },
  {
    _id: 'c58fa894-f34b-adb3-c153-c0d1894a416b',
    type: 'Image',
    tag: 'img',
    classes: [],
    children: [],
    data: {
      attr: {
        src: 'https://cdn.dev.website-files.com/66ec20b1768f52978c5cc466/66ec2165768f52978c5cc745_puppy-1502565_1280.jpg',
        loading: 'lazy',
        width: 'Auto',
        height: '200',
        alt: '__wf_reserved_inherit',
      },
      sizes: [
        {
          max: 479,
          size: '100vw',
        },
        {
          max: 10000,
          size: '300.1171875px',
        },
      ],
      srcsetDisabled: false,
    },
    bind: {
      type: 'Record',
      val: {
        img: {
          type: 'Select',
          val: {
            from: {
              type: 'Variable',
              val: 'data',
            },
            prop: '05bc9ceb-bfa3-f5b2-3e8c-8cef6d1a8517',
          },
        },
      },
    } as unknown as Expression,
  },
  {
    _id: component_ID_6,
    type: 'BlockContainer',
    tag: 'div',
    classes: [],
    children: [
      '1cd94f7d-4c4d-b81f-68c3-a708e7352cae',
      '13ad95e1-422e-762a-efed-aae837bfc6cd',
      'ee1ac65f-74c1-70de-3bbb-bd0ce9a53887',
    ],
    data: {
      grid: {
        type: 'container',
      },
      tag: 'div',
      sym: {
        root: true,
        name: 'c6',
        copies: {
          '66fb1cca6e9793603e84c6ce': 1,
        },
      },
    },
    dataType:
      '{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowCons","val":{"label":"f3dd3fc5-c18b-ea33-6393-a54ab10f806a","type":{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","List"]},"arg":{"type":"Element","meta":{"multiLine":false}}},"system":{"isBindable":true,"label":"Localized Prop 1","defaultValue":[{"id":"1cd94f7d-4c4d-b81f-68c3-a708e7352caf","children":[],"data":{"value":"This is the first localized default value text prop"},"pluginType":"Basic","text":true}]}},"tail":{"type":"RowEmpty"}}}}}',
  },
  {
    _id: '1cd94f7d-4c4d-b81f-68c3-a708e7352cae',
    type: 'Heading',
    tag: 'h1',
    classes: [
      'd1ba5bf9-4e61-31bf-b568-dcc2860196c9',
      'c9bc25f9-2c16-a06a-6866-029337e05337',
    ],
    children: [],
    data: {
      tag: 'h1',
    },
    bind: {
      type: 'Record',
      val: {
        children: {
          type: 'Select',
          val: {
            from: {
              type: 'Variable',
              val: 'data',
            },
            prop: 'f3dd3fc5-c18b-ea33-6393-a54ab10f806a',
          },
        },
      },
    } as unknown as Expression,
  },
  {
    _id: '13ad95e1-422e-762a-efed-aae837bfc6cd',
    type: 'Heading',
    tag: 'h1',
    classes: ['c9bc25f9-2c16-a06a-6866-029337e05337'],
    children: ['13ad95e1-422e-762a-efed-aae837bfc6ce'],
    data: {
      tag: 'h1',
    },
  },
  {
    _id: '13ad95e1-422e-762a-efed-aae837bfc6ce',
    text: true,
    v: 'This is the first localized static text',
  },
  {
    _id: 'ee1ac65f-74c1-70de-3bbb-bd0ce9a53887',
    type: 'Symbol',
    tag: 'div',
    classes: [],
    children: [],
    data: {
      sym: {
        inst: component_ID_7,
        instDataType:
          '{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowCons","val":{"label":"sym","type":{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowCons","val":{"label":"overrides","type":{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowCons","val":{"label":"ae9ce8ef-9715-3b42-0a51-8e65127d4b28","type":{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","List"]},"arg":{"type":"Element","meta":{"multiLine":false}}},"system":{"isBindable":true,"label":"Localized Prop 3","defaultValue":[{"id":"4b5b5b32-2ad3-7755-282b-e8cc63bcedca","children":[],"data":{"value":"This is the third localized default value text prop"},"pluginType":"Basic","text":true}],"isUnlinked":false,"isDefault":false}},"tail":{"type":"RowEmpty"}}}}},"tail":{"type":"RowEmpty"}}}}},"tail":{"type":"RowEmpty"}}}}}',
        overrides: {
          'ae9ce8ef-9715-3b42-0a51-8e65127d4b28': [
            '4b5b5b32-2ad3-7755-282b-e8cc63bcedca',
          ],
        },
      },
    },
  },
  {
    _id: '4b5b5b32-2ad3-7755-282b-e8cc63bcedca',
    text: true,
    v: 'This is the first localized text prop override',
  },
  {
    _id: component_ID_7,
    type: 'BlockContainer',
    tag: 'div',
    classes: [],
    children: [
      'f78b5bff-e1a1-9ace-36b7-a628b7bd3093',
      '4b5b5b32-2ad3-7755-282b-e8cc63bcedc9',
      '414c3edc-6795-c3f9-fdc5-6f85e4ce65e8',
      'c17d2541-698d-ec25-2411-63ae64a6ee9c',
      'c0e68469-1444-109e-9a4e-b7b1908f07a9',
    ],
    data: {
      grid: {
        type: 'container',
      },
      tag: 'div',
      sym: {
        root: true,
        name: 'c7',
      },
    },
    dataType:
      '{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowCons","val":{"label":"ae9ce8ef-9715-3b42-0a51-8e65127d4b28","type":{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","List"]},"arg":{"type":"Element","meta":{"multiLine":false}}},"system":{"isBindable":true,"label":"Localized Prop 3","defaultValue":[{"id":"4b5b5b32-2ad3-7755-282b-e8cc63bcedca","children":[],"data":{"value":"This is the third localized default value text prop"},"pluginType":"Basic","text":true}]}},"tail":{"type":"RowCons","val":{"label":"34e08d83-29d9-9661-2c57-9c1883bc1da2","type":{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","List"]},"arg":{"type":"Element","meta":{"multiLine":false}}},"system":{"isBindable":true,"label":"Localized Prop 2","defaultValue":[{"id":"f78b5bff-e1a1-9ace-36b7-a628b7bd3094","children":[],"data":{"value":"This is the second localized default value text prop"},"pluginType":"Basic","text":true}]}},"tail":{"type":"RowEmpty"}}}}}}}',
  },
  {
    _id: 'f78b5bff-e1a1-9ace-36b7-a628b7bd3093',
    type: 'Heading',
    tag: 'h1',
    classes: ['c9bc25f9-2c16-a06a-6866-029337e05337'],
    children: [],
    data: {
      tag: 'h1',
    },
    bind: {
      type: 'Record',
      val: {
        children: {
          type: 'Select',
          val: {
            from: {
              type: 'Variable',
              val: 'data',
            },
            prop: '34e08d83-29d9-9661-2c57-9c1883bc1da2',
          },
        },
      },
    } as unknown as Expression,
  },
  {
    _id: '4b5b5b32-2ad3-7755-282b-e8cc63bcedc9',
    type: 'Heading',
    tag: 'h1',
    classes: ['c9bc25f9-2c16-a06a-6866-029337e05337'],
    children: [],
    data: {
      tag: 'h1',
    },
    bind: {
      type: 'Record',
      val: {
        children: {
          type: 'Select',
          val: {
            from: {
              type: 'Variable',
              val: 'data',
            },
            prop: 'ae9ce8ef-9715-3b42-0a51-8e65127d4b28',
          },
        },
      },
    } as unknown as Expression,
  },
  {
    _id: '414c3edc-6795-c3f9-fdc5-6f85e4ce65e8',
    type: 'Heading',
    tag: 'h1',
    classes: ['c9bc25f9-2c16-a06a-6866-029337e05337'],
    children: ['414c3edc-6795-c3f9-fdc5-6f85e4ce65e9'],
    data: {
      tag: 'h1',
    },
  },
  {
    _id: '414c3edc-6795-c3f9-fdc5-6f85e4ce65e9',
    text: true,
    v: 'This is the second localized static text',
  },
  {
    _id: 'c17d2541-698d-ec25-2411-63ae64a6ee9c',
    type: 'Symbol',
    tag: 'div',
    classes: [],
    children: [],
    data: {
      sym: {
        inst: component_ID_8,
        instDataType:
          '{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowCons","val":{"label":"sym","type":{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowCons","val":{"label":"overrides","type":{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowCons","val":{"label":"f21fc95f-3fbb-e49d-7697-e68cf927b902","type":{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","List"]},"arg":{"type":"Element","meta":{"multiLine":false}}},"system":{"isBindable":true,"label":"Localized Prop 5","defaultValue":[{"id":"6893c772-0d47-d655-16e8-584d8bc06e10","children":[],"data":{"value":"This is the fifth localized default value text prop"},"pluginType":"Basic","text":true}],"isUnlinked":false,"isDefault":false}},"tail":{"type":"RowEmpty"}}}}},"tail":{"type":"RowEmpty"}}}}},"tail":{"type":"RowEmpty"}}}}}',
        overrides: {
          'f21fc95f-3fbb-e49d-7697-e68cf927b902': [
            '6893c772-0d47-d655-16e8-584d8bc06e10',
          ],
        },
      },
    },
  },
  {
    _id: '6893c772-0d47-d655-16e8-584d8bc06e10',
    text: true,
    v: 'This is a static localized override of a text prop on a nested component instance bound to another prop (prop drilling)',
  },
  {
    _id: 'c0e68469-1444-109e-9a4e-b7b1908f07a9',
    type: 'Symbol',
    tag: 'div',
    classes: [],
    children: [],
    data: {
      sym: {
        inst: component_ID_8,
      },
    },
  },
  {
    _id: component_ID_8,
    type: 'BlockContainer',
    tag: 'div',
    classes: ['c9bc25f9-2c16-a06a-6866-029337e05337'],
    children: [
      'ab1a07ee-4ccd-5386-dae3-e4409d8bd3f3',
      'bea74a5e-0c26-c9a4-9989-28ac1dc05151',
    ],
    data: {
      grid: {
        type: 'container',
      },
      tag: 'div',
      sym: {
        root: true,
        name: 'c8',
      },
    },
    dataType:
      '{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowCons","val":{"label":"f21fc95f-3fbb-e49d-7697-e68cf927b902","type":{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","List"]},"arg":{"type":"Element","meta":{"multiLine":false}}},"system":{"isBindable":true,"label":"Localized Prop 5","defaultValue":[{"id":"6893c772-0d47-d655-16e8-584d8bc06e10","children":[],"data":{"value":"This is the fifth localized default value text prop"},"pluginType":"Basic","text":true}],"isUnlinked":false,"isDefault":true}},"tail":{"type":"RowEmpty"}}}}}',
  },
  {
    _id: 'ab1a07ee-4ccd-5386-dae3-e4409d8bd3f3',
    type: 'Heading',
    tag: 'h1',
    classes: [],
    children: ['ab1a07ee-4ccd-5386-dae3-e4409d8bd3f4'],
    data: {
      tag: 'h1',
    },
  },
  {
    _id: 'ab1a07ee-4ccd-5386-dae3-e4409d8bd3f4',
    text: true,
    v: 'This is the third localized static text',
  },
  {
    _id: 'bea74a5e-0c26-c9a4-9989-28ac1dc05151',
    type: 'Symbol',
    tag: 'div',
    classes: [],
    children: [],
    data: {
      sym: {
        inst: component_ID_9,
        instDataType:
          '{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowCons","val":{"label":"sym","type":{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowCons","val":{"label":"overrides","type":{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowCons","val":{"label":"79faa1a3-0b34-f341-b923-fb5fe3c50db7","type":{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","List"]},"arg":{"type":"Element","meta":{"multiLine":false}}},"system":{"isBindable":true,"label":"Localized Prop 4","defaultValue":[{"id":"6893c772-0d47-d655-16e8-584d8bc06e10","children":[],"data":{"value":"This is the fourth localized default value text prop"},"pluginType":"Basic","text":true}],"isUnlinked":false,"isDefault":true}},"tail":{"type":"RowEmpty"}}}}},"tail":{"type":"RowEmpty"}}}}},"tail":{"type":"RowEmpty"}}}}}',
      },
    },
    bind: {
      type: 'Record',
      val: {
        sym: {
          type: 'Record',
          val: {
            overrides: {
              type: 'Record',
              val: {
                '79faa1a3-0b34-f341-b923-fb5fe3c50db7': {
                  type: 'Select',
                  val: {
                    from: {
                      type: 'Variable',
                      val: 'data',
                    },
                    prop: 'f21fc95f-3fbb-e49d-7697-e68cf927b902',
                  },
                },
              },
            },
          },
        },
      },
    } as unknown as Expression,
  },
  {
    _id: component_ID_9,
    type: 'BlockContainer',
    tag: 'div',
    classes: [],
    children: ['6893c772-0d47-d655-16e8-584d8bc06e0f'],
    data: {
      grid: {
        type: 'container',
      },
      tag: 'div',
      sym: {
        root: true,
        name: 'c9',
      },
    },
    dataType:
      '{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowCons","val":{"label":"79faa1a3-0b34-f341-b923-fb5fe3c50db7","type":{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","List"]},"arg":{"type":"Element","meta":{"multiLine":false}}},"system":{"isBindable":true,"label":"Localized Prop 4","defaultValue":[{"id":"6893c772-0d47-d655-16e8-584d8bc06e10","children":[],"data":{"value":"This is the fourth localized default value text prop"},"pluginType":"Basic","text":true}]}},"tail":{"type":"RowEmpty"}}}}}',
  },
  {
    _id: '6893c772-0d47-d655-16e8-584d8bc06e0f',
    type: 'Heading',
    tag: 'h1',
    classes: [],
    children: [],
    data: {
      tag: 'h1',
    },
    bind: {
      type: 'Record',
      val: {
        children: {
          type: 'Select',
          val: {
            from: {
              type: 'Variable',
              val: 'data',
            },
            prop: '79faa1a3-0b34-f341-b923-fb5fe3c50db7',
          },
        },
      },
    } as unknown as Expression,
  },
  {
    _id: component_ID_10,
    type: 'BlockContainer',
    tag: 'div',
    children: ['020237bc-46b3-358f-afe4-406f3c4a80b7'],
    data: {
      grid: {
        type: 'container',
      },
      tag: 'div',
      sym: {
        root: true,
        name: 'c10',
      },
    },
    dataType: JSON.stringify(
      TRecord({
        'd10f4fbe-f651-5953-242e-b428675976f0': withMeta(
          {type: 'image'},
          withSystem(
            {
              label: 'Localized Image Prop 2',
              isBindable: true,
              defaultValue: {
                id: asset_ID_7,
              },
              isDefault: false,
              isUnlinked: false,
            },
            TTypeConstructor(imageType)
          )
        ),
      })
    ),
  },
];

export const symbolsLocalizations: SymbolsLocalizationData = {
  fr: {
    'bfd3fec7-8d8d-129e-0def-7d2f1838d310': {
      _id: 'bfd3fec7-8d8d-129e-0def-7d2f1838d310',
      text: true,
      v: "Il s'agit d'un titre",
    },
    '23b40dfe-0251-af0e-4efd-e47f1aa8be3d': {
      _id: '23b40dfe-0251-af0e-4efd-e47f1aa8be3d',
      text: true,
      v: 'La localisation est très cool',
    },
    [component_ID_6]: {
      _id: component_ID_6,
      type: 'BlockContainer',
      tag: 'div',
      children: [
        '1cd94f7d-4c4d-b81f-68c3-a708e7352cae',
        '13ad95e1-422e-762a-efed-aae837bfc6cd',
        'ee1ac65f-74c1-70de-3bbb-bd0ce9a53887',
      ],
      data: {
        grid: {
          type: 'container',
        },
        tag: 'div',
        sym: {
          root: true,
          name: 'c6',
          copies: {
            '66fb1cca6e9793603e84c6ce': 1,
          },
        },
      },
      dataType:
        '{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowCons","val":{"label":"f3dd3fc5-c18b-ea33-6393-a54ab10f806a","type":{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","List"]},"arg":{"type":"Element","meta":{"multiLine":false}}},"system":{"isBindable":true,"label":"Localized Prop 1","defaultValue":[{"id":"1cd94f7d-4c4d-b81f-68c3-a708e7352caf","children":[],"data":{"value":"Il s\'agit du premier accessoire de texte à valeur par défaut localisé"},"pluginType":"Basic","text":true}]}},"tail":{"type":"RowEmpty"}}}}}',
    },
    '13ad95e1-422e-762a-efed-aae837bfc6ce': {
      _id: '13ad95e1-422e-762a-efed-aae837bfc6ce',
      text: true,
      v: "Il s'agit du premier texte statique localisé",
    },
    '4b5b5b32-2ad3-7755-282b-e8cc63bcedca': {
      _id: '4b5b5b32-2ad3-7755-282b-e8cc63bcedca',
      text: true,
      v: "Il s'agit du premier remplacement d'un accessoire de texte localisé",
    },
    [component_ID_7]: {
      _id: component_ID_7,
      type: 'BlockContainer',
      tag: 'div',
      children: [
        'f78b5bff-e1a1-9ace-36b7-a628b7bd3093',
        '4b5b5b32-2ad3-7755-282b-e8cc63bcedc9',
        '414c3edc-6795-c3f9-fdc5-6f85e4ce65e8',
        'c17d2541-698d-ec25-2411-63ae64a6ee9c',
        'c0e68469-1444-109e-9a4e-b7b1908f07a9',
      ],
      data: {
        grid: {
          type: 'container',
        },
        tag: 'div',
        sym: {
          root: true,
          name: 'c7',
        },
      },
      dataType:
        '{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowCons","val":{"label":"ae9ce8ef-9715-3b42-0a51-8e65127d4b28","type":{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","List"]},"arg":{"type":"Element","meta":{"multiLine":false}}},"system":{"isBindable":true,"label":"Localized Prop 3","defaultValue":[{"id":"4b5b5b32-2ad3-7755-282b-e8cc63bcedca","children":[],"data":{"value":"Il s\'agit du troisième accessoire de texte à valeur par défaut localisé"},"pluginType":"Basic","text":true}]}},"tail":{"type":"RowCons","val":{"label":"34e08d83-29d9-9661-2c57-9c1883bc1da2","type":{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","List"]},"arg":{"type":"Element","meta":{"multiLine":false}}},"system":{"isBindable":true,"label":"Localized Prop 2","defaultValue":[{"id":"f78b5bff-e1a1-9ace-36b7-a628b7bd3094","children":[],"data":{"value":"Il s\'agit du deuxième objet de texte à valeur par défaut localisé"},"pluginType":"Basic","text":true}]}},"tail":{"type":"RowEmpty"}}}}}}}',
    },
    '414c3edc-6795-c3f9-fdc5-6f85e4ce65e9': {
      _id: '414c3edc-6795-c3f9-fdc5-6f85e4ce65e9',
      text: true,
      v: "Il s'agit du deuxième texte statique localisé",
    },
    '6893c772-0d47-d655-16e8-584d8bc06e10': {
      _id: '6893c772-0d47-d655-16e8-584d8bc06e10',
      text: true,
      v: "Il s'agit d'un remplacement localisé statique d'un accessoire de texte sur une instance de composant imbriquée.",
    },
    [component_ID_8]: {
      _id: component_ID_8,
      type: 'BlockContainer',
      tag: 'div',
      children: [
        'ab1a07ee-4ccd-5386-dae3-e4409d8bd3f3',
        'bea74a5e-0c26-c9a4-9989-28ac1dc05151',
      ],
      data: {
        grid: {
          type: 'container',
        },
        tag: 'div',
        sym: {
          root: true,
          name: 'c8',
        },
      },
      dataType:
        '{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowCons","val":{"label":"f21fc95f-3fbb-e49d-7697-e68cf927b902","type":{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","List"]},"arg":{"type":"Element","meta":{"multiLine":false}}},"system":{"isBindable":true,"label":"Localized Prop 5","defaultValue":[{"id":"6893c772-0d47-d655-16e8-584d8bc06e10","children":[],"data":{"value":"Il s\'agit de la cinquième prop de texte à valeur par défaut localisée"},"pluginType":"Basic","text":true}],"isUnlinked":false,"isDefault":true}},"tail":{"type":"RowEmpty"}}}}}',
    },
    'ab1a07ee-4ccd-5386-dae3-e4409d8bd3f4': {
      _id: 'ab1a07ee-4ccd-5386-dae3-e4409d8bd3f4',
      text: true,
      v: "Il s'agit du troisième texte statique localisé",
    },
    [component_ID_9]: {
      _id: component_ID_9,
      type: 'BlockContainer',
      tag: 'div',
      children: ['6893c772-0d47-d655-16e8-584d8bc06e0f'],
      data: {
        grid: {
          type: 'container',
        },
        tag: 'div',
        sym: {
          root: true,
          name: 'c9',
        },
      },
      dataType:
        '{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowCons","val":{"label":"79faa1a3-0b34-f341-b923-fb5fe3c50db7","type":{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","List"]},"arg":{"type":"Element","meta":{"multiLine":false}}},"system":{"isBindable":true,"label":"Localized Prop 4","defaultValue":[{"id":"6893c772-0d47-d655-16e8-584d8bc06e10","children":[],"data":{"value":"Il s\'agit du quatrième accessoire de texte à valeur par défaut localisé"},"pluginType":"Basic","text":true}]}},"tail":{"type":"RowEmpty"}}}}}',
    },
  },
  pt: {
    'bfd3fec7-8d8d-129e-0def-7d2f1838d310': {
      _id: 'bfd3fec7-8d8d-129e-0def-7d2f1838d310',
      text: true,
      v: 'Este é um título',
    },
    '23b40dfe-0251-af0e-4efd-e47f1aa8be3d': {
      _id: '23b40dfe-0251-af0e-4efd-e47f1aa8be3d',
      text: true,
      v: 'A localização é muito legal',
    },
    '76ce35f2-d749-a2ae-1299-d6b51880dc45': {
      _id: '76ce35f2-d749-a2ae-1299-d6b51880dc45',
      type: 'Symbol',
      tag: 'div',
      children: [],
      data: {
        sym: {
          inst: component_ID_10,
          overrides: {
            'd10f4fbe-f651-5953-242e-b428675976f0': {
              id: asset_ID_5,
            },
          },
        },
      },
    },
    [component_ID_4]: {
      _id: component_ID_4,
      type: 'BlockContainer',
      tag: 'div',
      children: ['020237bc-46b3-358f-afe4-406f3c4a80b7'],
      data: {
        grid: {
          type: 'container',
        },
        tag: 'div',
        sym: {
          root: true,
          name: 'c2',
        },
      },
      dataType: JSON.stringify(
        TRecord({
          'd10f4fbe-f651-5953-242e-b428675976f0': withMeta(
            {type: 'image'},
            withSystem(
              {
                label: 'Localized Image Prop 2',
                isBindable: true,
                defaultValue: {
                  id: asset_ID_7,
                },
                isDefault: false,
                isUnlinked: false,
              },
              TTypeConstructor(imageType)
            )
          ),
        })
      ),
    },
    [component_ID_5]: {
      _id: component_ID_5,
      type: 'BlockContainer',
      tag: 'div',
      children: [
        '5fcba5d9-73a7-fcb1-96f8-e9aa6cb9fd35',
        'c58fa894-f34b-adb3-c153-c0d1894a416b',
      ],
      data: {
        grid: {
          type: 'container',
        },
        tag: 'div',
        sym: {
          root: true,
          name: 'c3',
        },
      },
      dataType: `{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowCons","val":{"label":"05bc9ceb-bfa3-f5b2-3e8c-8cef6d1a8517","type":{"type":"TypeConstructor","val":["Asset","Image"],"system":{"label":"Localized Image Prop 1","isBindable":true,"defaultValue":{"id":"${asset_ID_1}"}},"meta":{"type":"image"}},"tail":{"type":"RowEmpty"}}}}}`,
    },
    [component_ID_10]: {
      _id: component_ID_10,
      type: 'BlockContainer',
      tag: 'div',
      children: ['020237bc-46b3-358f-afe4-406f3c4a80b7'],
      data: {
        grid: {
          type: 'container',
        },
        tag: 'div',
        sym: {
          root: true,
          name: 'c2',
        },
      },
      dataType: `{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowCons","val":{"label":"sym","type":{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowCons","val":{"label":"overrides","type":{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowCons","val":{"label":"d10f4fbe-f651-5953-242e-b428675976f0","type":{"type":"TypeConstructor","val":["Asset","Image"],"system":{"label":"Localized Image Prop 2","isBindable":true,"defaultValue":{"id":"${asset_ID_7}"},"isDefault":false,"isUnlinked":false},"meta":{"type":"image"}},"tail":{"type":"RowEmpty"}}}}},"tail":{"type":"RowEmpty"}}}}},"tail":{"type":"RowEmpty"}}}}}`,
    },
    '5fcba5d9-73a7-fcb1-96f8-e9aa6cb9fd35': {
      _id: '5fcba5d9-73a7-fcb1-96f8-e9aa6cb9fd35',
      type: 'Image',
      tag: 'img',
      children: [],
      data: {
        img: {
          id: asset_ID_2,
        },
        attr: {
          src: 'https://cdn.dev.website-files.com/66ec3605c8bd3b3330903951/asset_ID_2_1693407567-funny-dog-costumes-harry-potter-wizard-costume-64ef59205a443.avif',
          loading: 'lazy',
          width: 'Auto',
          height: '200',
          alt: '__wf_reserved_inherit',
        },
        sizes: [
          {
            max: 10000,
            size: '200px',
          },
        ],
        srcsetDisabled: false,
      },
    },
    [component_ID_6]: {
      _id: component_ID_6,
      type: 'BlockContainer',
      tag: 'div',
      children: [
        '1cd94f7d-4c4d-b81f-68c3-a708e7352cae',
        '13ad95e1-422e-762a-efed-aae837bfc6cd',
        'ee1ac65f-74c1-70de-3bbb-bd0ce9a53887',
      ],
      data: {
        grid: {
          type: 'container',
        },
        tag: 'div',
        sym: {
          root: true,
          name: 'c6',
          copies: {
            '66fb1cca6e9793603e84c6ce': 1,
          },
        },
      },
      dataType:
        '{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowCons","val":{"label":"f3dd3fc5-c18b-ea33-6393-a54ab10f806a","type":{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","List"]},"arg":{"type":"Element","meta":{"multiLine":false}}},"system":{"isBindable":true,"label":"Localized Prop 1","defaultValue":[{"id":"1cd94f7d-4c4d-b81f-68c3-a708e7352caf","children":[],"data":{"value":"Esta é a primeira propriedade de texto de valor padrão localizada"},"pluginType":"Basic","text":true}]}},"tail":{"type":"RowEmpty"}}}}}',
    },
    '13ad95e1-422e-762a-efed-aae837bfc6ce': {
      _id: '13ad95e1-422e-762a-efed-aae837bfc6ce',
      text: true,
      v: 'Este é o primeiro texto estático localizado',
    },
    '4b5b5b32-2ad3-7755-282b-e8cc63bcedca': {
      _id: '4b5b5b32-2ad3-7755-282b-e8cc63bcedca',
      text: true,
      v: 'Esta é a primeira substituição de adereço de texto localizado',
    },
    [component_ID_7]: {
      _id: component_ID_7,
      type: 'BlockContainer',
      tag: 'div',
      children: [
        'f78b5bff-e1a1-9ace-36b7-a628b7bd3093',
        '4b5b5b32-2ad3-7755-282b-e8cc63bcedc9',
        '414c3edc-6795-c3f9-fdc5-6f85e4ce65e8',
        'c17d2541-698d-ec25-2411-63ae64a6ee9c',
        'c0e68469-1444-109e-9a4e-b7b1908f07a9',
      ],
      data: {
        grid: {
          type: 'container',
        },
        tag: 'div',
        sym: {
          root: true,
          name: 'c7',
        },
      },
      dataType:
        '{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowCons","val":{"label":"ae9ce8ef-9715-3b42-0a51-8e65127d4b28","type":{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","List"]},"arg":{"type":"Element","meta":{"multiLine":false}}},"system":{"isBindable":true,"label":"Localized Prop 3","defaultValue":[{"id":"4b5b5b32-2ad3-7755-282b-e8cc63bcedca","children":[],"data":{"value":"Esta é a terceira propriedade de texto de valor padrão localizada"},"pluginType":"Basic","text":true}]}},"tail":{"type":"RowCons","val":{"label":"34e08d83-29d9-9661-2c57-9c1883bc1da2","type":{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","List"]},"arg":{"type":"Element","meta":{"multiLine":false}}},"system":{"isBindable":true,"label":"Localized Prop 2","defaultValue":[{"id":"f78b5bff-e1a1-9ace-36b7-a628b7bd3094","children":[],"data":{"value":"Esta é a segunda propriedade de texto de valor padrão localizada1"},"pluginType":"Basic","text":true}]}},"tail":{"type":"RowEmpty"}}}}}}}',
    },
    '414c3edc-6795-c3f9-fdc5-6f85e4ce65e9': {
      _id: '414c3edc-6795-c3f9-fdc5-6f85e4ce65e9',
      text: true,
      v: 'Este é o segundo texto estático localizado',
    },
    '6893c772-0d47-d655-16e8-584d8bc06e10': {
      _id: '6893c772-0d47-d655-16e8-584d8bc06e10',
      text: true,
      v: 'Essa é uma substituição estática e localizada de um suporte de texto em uma instância de componente aninhada',
    },
    [component_ID_8]: {
      _id: component_ID_8,
      type: 'BlockContainer',
      tag: 'div',
      children: [
        'ab1a07ee-4ccd-5386-dae3-e4409d8bd3f3',
        'bea74a5e-0c26-c9a4-9989-28ac1dc05151',
      ],
      data: {
        grid: {
          type: 'container',
        },
        tag: 'div',
        sym: {
          root: true,
          name: 'c8',
        },
      },
      dataType:
        '{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowCons","val":{"label":"f21fc95f-3fbb-e49d-7697-e68cf927b902","type":{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","List"]},"arg":{"type":"Element","meta":{"multiLine":false}}},"system":{"isBindable":true,"label":"Localized Prop 5","defaultValue":[{"id":"6893c772-0d47-d655-16e8-584d8bc06e10","children":[],"data":{"value":"Esta é a quinta propriedade de texto de valor padrão localizada"},"pluginType":"Basic","text":true}],"isUnlinked":false,"isDefault":true}},"tail":{"type":"RowEmpty"}}}}}',
    },
    'ab1a07ee-4ccd-5386-dae3-e4409d8bd3f4': {
      _id: 'ab1a07ee-4ccd-5386-dae3-e4409d8bd3f4',
      text: true,
      v: 'Este é o terceiro texto estático localizado',
    },
    [component_ID_9]: {
      _id: component_ID_9,
      type: 'BlockContainer',
      tag: 'div',
      children: ['6893c772-0d47-d655-16e8-584d8bc06e0f'],
      data: {
        grid: {
          type: 'container',
        },
        tag: 'div',
        sym: {
          root: true,
          name: 'c9',
        },
      },
      dataType:
        '{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","Record"]},"arg":{"type":"RowCons","val":{"label":"79faa1a3-0b34-f341-b923-fb5fe3c50db7","type":{"type":"TypeApplication","val":{"con":{"type":"TypeConstructor","val":["Builtin","List"]},"arg":{"type":"Element","meta":{"multiLine":false}}},"system":{"isBindable":true,"label":"Localized Prop 4","defaultValue":[{"id":"6893c772-0d47-d655-16e8-584d8bc06e10","children":[],"data":{"value":"Esta é a quarta propriedade de texto de valor padrão localizada"},"pluginType":"Basic","text":true}]}},"tail":{"type":"RowEmpty"}}}}}',
    },
  },
};

export const content: LibraryContent = {
  symbols,
  styles: {
    data: {
      swatches: [],
      breakpoints: {
        main: {maxWidth: 10000},
        medium: {maxWidth: 991},
        small: {maxWidth: 767},
        tiny: {maxWidth: 479},
      },
      macros: [],
      migrations: {stylesNext: true},
      appliedStylesMap: {},
    },
    blocks: [],
  },
  assets: Object.values(assets),
  ix2: {events: [], actionLists: [], interactions: []},
  variables: [],
  variableCollections: [],
  customFonts: {
    typeKitKitId: '',
    typeKitKitData: {},
    googleFonts: [],
    otherFonts: [],
    fallbacks: {},
  },
  symbolsLocalizations,
};

const libraryExport_Component_1: ComponentExport = {
  type: LIBRARY_EXPORT_TYPES.COMPONENT,
  metadata: {
    targetComponentId: component_ID_1,
    displayName: 'c1',
    localized: true,
  },
};

const libraryExport_Component_2: ComponentExport = {
  type: LIBRARY_EXPORT_TYPES.COMPONENT,
  metadata: {
    targetComponentId: component_ID_2,
    displayName: 'c2',
    localized: true,
  },
};

const libraryExport_Component_3: ComponentExport = {
  type: LIBRARY_EXPORT_TYPES.COMPONENT,
  metadata: {
    targetComponentId: component_ID_3,
    displayName: 'c3',
    localized: true,
  },
};

const libraryExport_Component_4: ComponentExport = {
  type: LIBRARY_EXPORT_TYPES.COMPONENT,
  metadata: {
    targetComponentId: component_ID_4,
    displayName: 'c4',
    localized: true,
  },
};

const libraryExport_Component_5: ComponentExport = {
  type: LIBRARY_EXPORT_TYPES.COMPONENT,
  metadata: {
    targetComponentId: component_ID_5,
    displayName: 'c5',
    localized: true,
  },
};

const libraryExport_Component_6: ComponentExport = {
  type: LIBRARY_EXPORT_TYPES.COMPONENT,
  metadata: {
    targetComponentId: component_ID_6,
    displayName: 'c6',
    localized: true,
  },
};

const libraryExport_Component_7: ComponentExport = {
  type: LIBRARY_EXPORT_TYPES.COMPONENT,
  metadata: {
    targetComponentId: component_ID_7,
    displayName: 'c7',
    localized: true,
  },
};

const libraryExport_Component_8: ComponentExport = {
  type: LIBRARY_EXPORT_TYPES.COMPONENT,
  metadata: {
    targetComponentId: component_ID_8,
    displayName: 'c8',
    localized: true,
  },
};

const libraryExport_Component_9: ComponentExport = {
  type: LIBRARY_EXPORT_TYPES.COMPONENT,
  metadata: {
    targetComponentId: component_ID_9,
    displayName: 'c9',
    localized: true,
  },
};

const libraryExport_Component_10: ComponentExport = {
  type: LIBRARY_EXPORT_TYPES.COMPONENT,
  metadata: {
    targetComponentId: component_ID_10,
    displayName: 'c10',
    localized: true,
  },
};

export const componentExports = {
  libraryExport_Component_1,
  libraryExport_Component_2,
  libraryExport_Component_3,
  libraryExport_Component_4,
  libraryExport_Component_5,
  libraryExport_Component_6,
  libraryExport_Component_7,
  libraryExport_Component_8,
  libraryExport_Component_9,
  libraryExport_Component_10,
};
